import type { ClientExceptionEntityTypeEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { TFunction } from 'i18next';

import type { ApiLocalization } from '@/features/api/localization/apiLocalization';
import type { FlattenKeys } from '@/types';

type ApiLocalizationKeys = FlattenKeys<ApiLocalization>;

/**
 * Maps a backend `ClientExceptionType` (from the `type` field of the error response)
 * to an i18n key in the `api` namespace.
 *
 * Returns `undefined` when no specific mapping exists — callers should then fall back to
 * the generic `'error'` key.
 *
 * ### How to extend
 * 1. Add a new `ClientExceptionType` entry to the backend enum.
 * 2. Add the corresponding i18n string to `apiLocalization.ts` (DE + EN).
 * 3. Add a case here.
 */
export const resolveOrganizationApiErrorKey = (
  type: ClientExceptionEntityTypeEnum | undefined
): ApiLocalizationKeys | undefined => {
  // Cast to string to support new backend ClientExceptionType values
  // that may not yet be in the generated API package.
  switch (type as string | undefined) {
    case 'TERRITORIAL_AUTHORITY_ALREADY_EXISTS': {
      return 'organization.errorTaoAlreadyExists';
    }
    case 'ORGANIZATION_NAME_ALREADY_EXISTS_IN_GEO_AREA': {
      return 'organization.errorNameAlreadyExistsInGeoArea';
    }
    case 'ORGANIZATION_ADMIN_NOT_FOUND': {
      return 'organization.errorAdminNotFound';
    }
    case 'ORGANIZATION_ADMIN_NOT_VERIFIED':
    case 'PERSON_EMAIL_NOT_VERIFIED': {
      return 'organization.errorAdminNotVerified';
    }
    case 'ORGANIZATION_TAG_NOT_ALLOWED': {
      return 'organization.errorTagNotAllowed';
    }
    case 'ORGANIZATION_GEO_AREA_NOT_IN_SCOPE': {
      return 'organization.errorGeoAreaNotInScope';
    }
    case 'NOT_AUTHORIZED': {
      return 'organization.errorNotAuthorized';
    }
    default: {
      return undefined;
    }
  }
};

/**
 * Resolves a user-facing error message for an organisation API error.
 * Falls back to the given `fallbackKey` when no specific mapping exists.
 */
export const resolveOrganizationApiError = (
  error: unknown,
  t: TFunction<'api'>,
  fallbackKey: ApiLocalizationKeys = 'error'
): string => {
  const type = extractExceptionType(error);
  const specificKey = resolveOrganizationApiErrorKey(type);
  return t(specificKey ?? fallbackKey);
};

const extractExceptionType = (
  error: unknown
): ClientExceptionEntityTypeEnum | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const e = error as Record<string, unknown>;
  return e['type'] as ClientExceptionEntityTypeEnum | undefined;
};
